import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useOutletContext, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import VisitAdmin from "../../components/VisitAdmin";
import Cookies from "js-cookie";
import {
  fetchTopProducts,
  fetchChildItems,
} from "../../store/reducers/productSlice";
import useAuthModalsStore from "../../store/reducers/useAuthModalsStore";
import Demo from "../../components/Demo";

const Tag = () => {
  const { open } = useOutletContext();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { product, subproduct } = useParams();
  const token = Cookies.get("_d_token");
  const rawItems = useSelector((state) => state.products.rawItems);
  const rawChildrenMap = useSelector((state) => state.products.rawChildrenMap);
  const { demoOpen, setDemoOpen } = useAuthModalsStore();

  useEffect(() => {
    if (!rawItems.length) {
      dispatch(fetchTopProducts());
    }
  }, [dispatch, rawItems.length]);

  const signature = Cookies.get("c_d_token");
  const { setLoginOpen } = useAuthModalsStore();

  const handleViewInPostman = () => {
    // Check if user is logged in (either token or signature)
    if (signature || token) {
      // User is logged in, open Postman link
      window.open(selectedSubItem.collection_url, "_blank");
    } else {
      // User is not logged in, open login modal
      setLoginOpen(true);
    }
  };

  // Load children (tags or endpoints) for current product
  useEffect(() => {
    const loadChildren = async () => {
      const parentProduct = rawItems.find(
        (item) =>
          item.product_name.toLowerCase() ===
          decodeURIComponent(product).toLowerCase()
      );

      if (
        parentProduct &&
        !rawChildrenMap[`product-${parentProduct.product_id}`]
      ) {
        await dispatch(
          fetchChildItems({
            type: "product",
            id: parentProduct.product_id,
          })
        );
      }
    };

    loadChildren();
  }, [dispatch, rawItems, rawChildrenMap, product]);

  const parentProduct = rawItems.find(
    (item) =>
      item.product_name.toLowerCase() ===
      decodeURIComponent(product).toLowerCase()
  );
  const uid = parentProduct ? `product-${parentProduct.product_id}` : null;

  const children = uid ? rawChildrenMap[uid] : null;
  const selectedSubItem = children?.find((child) => {
    return (
      child.tag_name?.toLowerCase() ===
        decodeURIComponent(subproduct).toLowerCase() ||
      child.endpoint_name?.toLowerCase() ===
        decodeURIComponent(subproduct).toLowerCase()
    );
  });

  useEffect(() => {
    if (selectedSubItem?.documentation) {
      const container = document.getElementById("tag-doc-container");
      if (container) {
        container.innerHTML = selectedSubItem.documentation;

        // find all <script> tags and execute them
        const scripts = container.getElementsByTagName("script");
        for (let i = 0; i < scripts.length; i++) {
          const script = document.createElement("script");
          script.text = scripts[i].text;
          document.body.appendChild(script);
          document.body.removeChild(script);
        }
      }
    }
  }, [selectedSubItem]);

  // If not ready, show loading state
  if (!parentProduct || !selectedSubItem) {
    return <Typography sx={{ p: 3 }}>Loading tag data...</Typography>;
  }

  // console.log(selectedSubItem)

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          p: isMobile ? 2 : 3,
          width: isMobile
            ? "100%"
            : open
            ? "calc(100% - 280px)"
            : "calc(100% - 100px)",
          marginLeft: isMobile ? "0" : open ? "280px" : "100px",
          transition: "margin 0.3s ease",
          height: "100vh",
          display: "flex",
          background: "#f9f9f9",
        }}
      >
        <Box
          mt={10}
          style={{
            textAlign: "center",
            width: "100%",
            marginLeft: open ? "" : "1em",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "20px",
              color: "#6C6C6C",
              textAlign: "left",
              mb: "15px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {parentProduct?.product_name}
          </Typography>
          <Box
            sx={{
              borderRadius: "12px",
              background: "#fff",
              height: "calc(100% - 28px)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: isMobile ? "15px 10px" : "30px 25px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 500,
                  lineHeight: "20px",
                  color: "#1D1D1D",
                }}
              >
                {selectedSubItem?.tag_name}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                {!token &&
                  parentProduct?.sso_path &&
                  parentProduct?.sso_path?.toLowerCase() !== "none" && (
                    <VisitAdmin
                      productData={parentProduct}
                      selectedSubItem={selectedSubItem}
                    />
                  )}
                {selectedSubItem?.demo_url && !isMobileOrTablet && (
                  <Button
                    variant="contained"
                    sx={{
                      width: "154px",
                      height: 42,
                      color: "#185CB7",
                      background: "#ffffff",
                      textTransform: "none",
                      fontSize: "16px",
                      borderRadius: "10px",
                      border: "1px solid #185CB7",
                      boxShadow: "none",
                      fontWeight: "600",
                      "&:hover": {
                        background: "#ffffff",
                        color: "#185CB7",
                        borderColor: "#185CB7",
                      },
                      gap: "13px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => setDemoOpen(true)}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_3000_11689)">
                        <circle
                          cx="9.99935"
                          cy="9.99935"
                          r="8.33333"
                          stroke="#185CB7"
                          stroke-width="1.5"
                        />
                        <path
                          d="M12.8447 9.11688C13.4962 9.50151 13.4962 10.4972 12.8447 10.8818L8.91124 13.2042C8.27809 13.578 7.5 13.0915 7.5 12.3217L7.5 7.67698C7.5 6.90724 8.27809 6.42069 8.91124 6.79451L12.8447 9.11688Z"
                          fill="#185CB7"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_3000_11689">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    Play Demo
                  </Button>
                )}
                {selectedSubItem?.collection_url && !isMobileOrTablet && (
                  <Button
                    variant="contained"
                    sx={{
                      minWidth: "154px",
                      height: 42,
                      color: "#185CB7",
                      background: "#ffffff",
                      textTransform: "none",
                      fontSize: "16px",
                      borderRadius: "10px",
                      border: "1px solid #185CB7",
                      boxShadow: "none",
                      fontWeight: "600",
                      "&:hover": {
                        background: "#ffffff",
                        color: "#185CB7",
                        borderColor: "#185CB7",
                      },
                      gap: "13px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={handleViewInPostman}
                  >
                    <svg
                      width="22"
                      height="14"
                      viewBox="0 0 22 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M17 3L19.4531 5.11438C20.4844 6.00327 21 6.44772 21 7C21 7.55228 20.4844 7.99673 19.4531 8.88562L17 11"
                        stroke="#185CB7"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M5 3L2.54692 5.11438C1.51564 6.00327 1 6.44772 1 7C1 7.55228 1.51564 7.99673 2.54692 8.88562L5 11"
                        stroke="#185CB7"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M13 1L9 13"
                        stroke="#185CB7"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    View in Postman
                  </Button>
                )}
              </Box>
            </Box>
            <Divider />
            <Box
              sx={{
                p: isMobile ? "15px 10px" : "15px 25px",
                overflow: "auto",
                height: "calc(100% - 96px)",
              }}
            >
              {/* <Box sx={{ fontSize: "15px", textAlign: "left" }} dangerouslySetInnerHTML={{ __html: selectedSubItem?.documentation }}/> */}
              <Box
                id="tag-doc-container"
                sx={{ fontSize: "15px", textAlign: "left" }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {demoOpen && (
        <Demo
          demoOpen={demoOpen}
          setDemoOpen={setDemoOpen}
          selectedSubItem={selectedSubItem}
        />
      )}
    </>
  );
};

export default Tag;
